<template>
    <v-card v-if="penaltyForEdit" elevation="0">
        <v-card-text>
            <v-form
                ref="form"
                v-model="isValidGeneral"
                :disabled="isSaveLoading"
                data-cy="product-prices-penalties-form"
            >
                <v-row dense>
                    <v-col cols="12" md="6">
                        <v-text-field
                            v-model="penaltyForEdit.name"
                            :rules="[requiredRule('name')]"
                            density="compact"
                            hide-details
                            :label="`${$t('name')}*`"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <div class="full-width d-flex mt-1">
                            <v-spacer />
                            <v-switch
                                v-model="penaltyForEdit.active"
                                color="primary"
                                hide-details
                                :label="$t('active')"
                            />
                        </div>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12" md="12">
                        <v-card variant="outlined">
                            <v-card-title>
                                <span class="font-weight-bold text-subtitle-2">
                                    {{ $t('penalty_conditions') }}
                                </span>
                            </v-card-title>
                            <v-card-text>
                                <v-row v-for="(item, index) in penaltyForEdit.penaltyConditions" :key="index" dense>
                                    <v-col cols="12" md="11">
                                        <div class="d-inline-flex flex-wrap align-center">
                                            <span class="me-2">
                                                {{ $t('penalty_cancel_description.when_cancelled') }}
                                            </span>
                                            <v-select
                                                v-model="item.cancelledDays"
                                                :items="daysSelectItems"
                                                class="me-2"
                                                item-value="value"
                                                item-title="title"
                                                hide-details
                                                variant="outlined"
                                                :rules="[requiredRule('')]"
                                            />
                                            <span class="me-2">
                                                {{ $t('penalty_cancel_description.days_before') }},
                                                {{ $t('penalty_cancel_description.amount_of_cancellation') }}
                                            </span>
                                            <NumberInputWithButtons
                                                v-model="item.price"
                                                :label="
                                                    item.unitOfMeasurement === 'PERCENT' ? $t('percent') : $t('amount')
                                                "
                                                class="me-2"
                                                :width="180"
                                                :buttons="[
                                                    {
                                                        icon: 'mdi-percent',
                                                        active: item.unitOfMeasurement === 'PERCENT',
                                                        onClick: () => (item.unitOfMeasurement = 'PERCENT'),
                                                    },
                                                    {
                                                        icon: 'mdi-cash',
                                                        active: item.unitOfMeasurement === 'FIXED_VALUE',
                                                        onClick: () => (item.unitOfMeasurement = 'FIXED_VALUE'),
                                                    },
                                                ]"
                                                :append-text="
                                                    item.unitOfMeasurement === 'FIXED_VALUE'
                                                        ? selectedTariffCurrencyCode
                                                        : null
                                                "
                                            />
                                            <template v-if="item.unitOfMeasurement === 'PERCENT'">
                                                <v-select
                                                    v-if="
                                                        contract.serviceType === contractServiceType.ownHotel ||
                                                        contract.serviceType === contractServiceType.ownCarRent
                                                    "
                                                    v-model="item.priceType"
                                                    hide-details
                                                    :label="$t('penalty_cancel_description.from_the')"
                                                    :items="conditionPriceTypeItems"
                                                />
                                                <span v-else>
                                                    {{ $t('penalty_cancel_description.from_the') }}
                                                    {{ $t('penalty_conditions_type.WHOLE_ORDER') }}
                                                </span>
                                            </template>
                                        </div>
                                    </v-col>
                                    <v-col cols="12" md="1">
                                        <div class="d-flex full-width align-center">
                                            <v-spacer />
                                            <v-btn
                                                v-if="index !== 0"
                                                size="small"
                                                color="error"
                                                variant="text"
                                                icon="mdi-close"
                                                @click="removePenaltyCondition(index)"
                                            />
                                        </div>
                                    </v-col>
                                </v-row>
                                <v-divider class="my-4" />
                                <v-row dense>
                                    <v-col cols="12">
                                        <div class="full-width d-flex align-center">
                                            <v-btn
                                                variant="flat"
                                                size="small"
                                                color="primary"
                                                @click="addPenaltyCondition"
                                            >
                                                {{ $t('add') }}
                                            </v-btn>
                                        </div>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
                <NoShowPenaltyConditions
                    v-if="isShowNoShowPenaltyConditions && penaltyForEdit.noShowPenaltyCondition"
                    v-model="penaltyForEdit.noShowPenaltyCondition"
                    :currency-code="selectedTariffCurrencyCode"
                    :condition-price-type-items="conditionPriceTypeItems"
                />
                <v-row dense>
                    <v-col cols="12" md="12">
                        <v-card variant="outlined" :loading="isTariffsLoading">
                            <v-card-title>
                                <span class="font-weight-bold text-subtitle-2">
                                    {{ $t('penalties_active_during') }}
                                </span>
                            </v-card-title>
                            <v-card-text>
                                <v-data-table
                                    :items="penaltyForEdit.tariffLinks"
                                    :headers="headersTariffs"
                                    :hide-default-header="isMobile"
                                    hide-default-footer
                                    :items-per-page="-1"
                                >
                                    <template v-slot:item.tariff="{item}">
                                        <div class="my-3">
                                            <v-select
                                                v-model="item.tariffId"
                                                :items="tariffItems(item.tariffId)"
                                                item-value="id"
                                                item-title="name"
                                                hide-details
                                                :density="'compact'"
                                                :disabled="item.tariffId && !tariffStatus(item.tariffId)"
                                                @update:model-value="updateTariff(item)"
                                            />
                                        </div>
                                    </template>
                                    <template v-slot:item.active="{item}">
                                        {{ tariffStatus(item.tariffId) ? $t('active') : $t('inactive') }}
                                    </template>
                                    <template v-slot:item.dates="{item}">
                                        <div class="d-flex align-center">
                                            <div class="me-4 full-width">
                                                <date-picker
                                                    v-model="item.dateFrom"
                                                    outlined
                                                    :density="'compact'"
                                                    :max="item.dateTo || '2100-01-01'"
                                                    :label="$t('date_from') + '*'"
                                                    :rules="[requiredRule('date_from')]"
                                                    hide-details
                                                />
                                            </div>
                                            <div class="full-width">
                                                <date-picker
                                                    v-model="item.dateTo"
                                                    outlined
                                                    :min="item.dateFrom || '1900-01-01'"
                                                    :density="'compact'"
                                                    :label="$t('date_to') + '*'"
                                                    :rules="[requiredRule('date_to')]"
                                                    hide-details
                                                />
                                            </div>
                                        </div>
                                    </template>
                                    <template v-slot:item.actions="{index}">
                                        <div class="full-width d-flex align-center">
                                            <v-spacer />
                                            <v-btn
                                                size="small"
                                                color="error"
                                                variant="text"
                                                icon="mdi-close"
                                                @click="removeTariffLink(index)"
                                            />
                                        </div>
                                    </template>
                                    <template v-slot:item.mobile="{item, index}">
                                        <v-col cols="12" class="mt-4 px-0">
                                            <v-select
                                                v-model="item.tariffId"
                                                :items="tariffItems(item.tariffId)"
                                                item-value="id"
                                                item-title="name"
                                                hide-details
                                                :density="'compact'"
                                                :disabled="item.tariffId && !tariffStatus(item.tariffId)"
                                            />
                                            <div class="text-caption">
                                                {{ tariffStatus(item.tariffId) ? $t('active') : $t('inactive') }}
                                            </div>
                                        </v-col>
                                        <v-col cols="12" class="px-0">
                                            <date-picker
                                                v-model="item.dateFrom"
                                                outlined
                                                :density="'compact'"
                                                :max="item.dateTo || '2100-01-01'"
                                                :label="$t('date_from') + '*'"
                                                :rules="[requiredRule('date_from')]"
                                                hide-details
                                            />
                                        </v-col>
                                        <v-col cols="12" class="px-0">
                                            <date-picker
                                                v-model="item.dateTo"
                                                outlined
                                                :min="item.dateFrom || '1900-01-01'"
                                                :density="'compact'"
                                                :label="$t('date_to') + '*'"
                                                :rules="[requiredRule('date_to')]"
                                                hide-details
                                            />
                                        </v-col>
                                        <v-col cols="12" class="px-0">
                                            <div class="full-width d-flex align-center">
                                                <v-spacer />
                                                <v-btn
                                                    size="small"
                                                    color="error"
                                                    variant="text"
                                                    icon="mdi-close"
                                                    @click="removeTariffLink(index)"
                                                />
                                            </div>
                                        </v-col>
                                    </template>
                                </v-data-table>
                                <v-divider class="mb-4" />
                                <p
                                    v-if="penaltyForEdit?.tariffLinks?.length && !checkCurrencyConsistency"
                                    class="text-subtitle-2 pb-4 text-red-darken-4 font-weight-regular"
                                >
                                    {{ $t('tariffs_validation_error') }}
                                </p>
                                <v-row dense>
                                    <v-col cols="12">
                                        <div class="full-width d-flex align-center">
                                            <v-btn variant="flat" size="small" color="primary" @click="addTariffLink">
                                                {{ $t('add') }}
                                            </v-btn>
                                        </div>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12" md="12">
                        <v-card variant="outlined">
                            <v-card-title>
                                <span class="font-weight-bold text-subtitle-2">
                                    {{ $t('description_internal_use') }}
                                </span>
                            </v-card-title>
                            <v-card-text>
                                <v-textarea
                                    v-model="penaltyForEdit.internalDescription"
                                    :placeholder="`${$t('description_internal_use')}`"
                                    rows="3"
                                />
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12" md="12">
                        <v-card variant="outlined">
                            <v-card-title>
                                <span class="font-weight-bold text-subtitle-2">
                                    {{ $t('description_for_tourists') }}
                                </span>
                            </v-card-title>
                            <v-card-text>
                                <v-textarea
                                    v-model="penaltyForEdit.externalDescription[selectedLocale]"
                                    :placeholder="`${$t('description_for_tourists')}`"
                                    rows="3"
                                />
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-form>
        </v-card-text>
        <v-card-actions class="d-flex justify-start px-6 pb-6 pt-4">
            <v-btn
                class="save-btn"
                color="primary"
                :loading="isSaveLoading"
                data-cy="save"
                variant="elevated"
                @click="save"
            >
                {{ $t('save') }}
            </v-btn>
            <v-btn class="cancel-btn" data-cy="close" variant="text" @click="onClose">
                {{ $t('cancel') }}
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script lang="ts">
    import {Component, Emit, mixins, Prop} from 'vue-facing-decorator'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'
    import DatePicker from '@/components/snippets/forms/DatePicker.vue'
    import DaterangeM from '~src/components/search/forms/daterangeM.src'
    import {clone} from '~/utils/helpers'
    import {contractServiceType} from '~/utils/types/types'

    @Component({
        components: {DaterangeM, DatePicker},
    })
    export class ProductPricesPenaltiesModal extends mixins(FormValidationRulesMixin) {
        @Prop() penalty
        @Prop() contract
        @Prop() selectedLocale
        @Prop() conditionPriceTypeItems

        isValidGeneral = false

        penaltyForEdit = null

        isSaveLoading = false
        isTariffsLoading = false
        activeTariffs = []
        inactiveTariffs = []

        editMode = false

        async mounted() {
            await this.initValues()
        }

        async initValues() {
            if (this.penalty) {
                this.penaltyForEdit = clone(this.penalty)
            } else {
                this.penaltyForEdit = {
                    contractId: this.contract.id,
                    name: '',
                    externalDescription: {},
                    internalDescription: '',
                    active: true,
                    penaltyConditions: [],
                    tariffLinks: [],
                }
                if (this.isShowNoShowPenaltyConditions) {
                    this.penaltyForEdit.noShowPenaltyCondition = {
                        price: 0,
                        cancelledDays: 0,
                        priceType: 'WHOLE_ORDER',
                        unitOfMeasurement: 'PERCENT',
                    }
                }
                this.addPenaltyCondition()
            }

            this.editMode = !!this.penaltyForEdit.id

            await Promise.all([this.loadTariffs()])

            if (this.editMode === false) {
                if (!this.penaltyForEdit.tariffLinks.length) {
                    this.addTariffLink()
                }
            }
        }

        async loadTariffs() {
            try {
                this.isTariffsLoading = true
                const [activeTariffs, inactiveTariffs] = await Promise.all([
                    this.$api.tariffs.get({contractId: this.contract.id, active: true}),
                    this.$api.tariffs.get({contractId: this.contract.id, active: false}),
                ])
                this.activeTariffs = activeTariffs.tariffs
                this.inactiveTariffs = inactiveTariffs.tariffs
            } catch (e) {
                this.$toast.error(this.$t('error_message.load_error'))
            } finally {
                this.isTariffsLoading = false
            }
        }

        clear() {
            this.penaltyForEdit = null
            this.activeTariffs = []
            this.inactiveTariffs = []
        }

        get tariffs() {
            return [...this.activeTariffs, ...this.inactiveTariffs]
        }

        tariffItems(id) {
            return !id || this.tariffStatus(id) ? this.activeTariffs : this.inactiveTariffs
        }

        tariffStatus(id) {
            return !!this.tariffs.find(tariff => tariff.id === id)?.active
        }

        get selectedTariffCurrencyCode() {
            const {tariffId} = this.penaltyForEdit?.tariffLinks?.[0] ?? {}
            return this.tariffs.find(({id}) => id === tariffId)?.currencyCode ?? 'USD'
        }

        @Emit()
        onClose() {
            this.clear()
        }

        async save() {
            await this.$refs.form.validate()
            if (!this.isValidGeneral || !this.checkCurrencyConsistency) return

            if (this.editMode) {
                await this.saveEdit()
            } else {
                await this.saveCreate()
            }
        }

        @Emit()
        onSave() {}

        async saveEdit() {
            try {
                this.isSaveLoading = true

                await this.$api.ownProductPenalties.put(this.penaltyForEdit)

                this.$toast.success(this.$t('success_message.saved_successfully'))
                this.onClose()
                this.onSave()
            } catch (e) {
                this.$toast.error(this.$t('error_message.save_error'))
            } finally {
                this.isSaveLoading = false
            }
        }

        async saveCreate() {
            try {
                this.isSaveLoading = true

                await this.$api.ownProductPenalties.post(this.penaltyForEdit)

                this.$toast.success(this.$t('success_message.saved_successfully'))
                this.onClose()
                this.onSave()
            } catch (e) {
                this.$toast.error(this.$t('error_message.save_error'))
            } finally {
                this.isSaveLoading = false
            }
        }

        removePenaltyCondition(index) {
            this.penaltyForEdit.penaltyConditions.splice(index, 1)
        }

        addPenaltyCondition() {
            this.penaltyForEdit.penaltyConditions.push({
                cancelledDays: null,
                price: 0,
                priceType: 'WHOLE_ORDER',
                unitOfMeasurement: 'PERCENT',
            })
        }

        removeTariffLink(tariffLinkIndex) {
            this.penaltyForEdit.tariffLinks.splice(tariffLinkIndex, 1)
        }

        addTariffLink() {
            if (this.activeTariffs.length === 0) return

            this.penaltyForEdit.tariffLinks.push({
                tariffId: this.activeTariffs[0].id,
                dateFrom: this.activeTariffs[0].dateFrom,
                dateTo: this.activeTariffs[0].dateTo,
                priority: this.penaltyForEdit.tariffLinks.length + 1,
            })
        }

        get isShowNoShowPenaltyConditions() {
            return this.contract.serviceType === contractServiceType.ownHotel
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        get headersTariffs() {
            if (this.isMobile) {
                return [{title: '', value: 'mobile'}]
            }
            return [
                {title: this.$t('tariff'), value: 'tariff'},
                {title: this.$t('status'), value: 'active'},
                {title: this.$t('dates'), value: 'dates'},
                {title: this.$t('actions'), value: 'actions', align: 'end'},
            ]
        }

        get daysSelectItems() {
            const arr = Array.from(Array(121).keys())
            arr.push(-1)
            return arr.map(number => ({
                value: number,
                title: number === -1 ? this.$t('any_of') : number,
            }))
        }

        get checkCurrencyConsistency() {
            const currencySet = new Set()

            this.penaltyForEdit?.tariffLinks?.forEach(selected => {
                const tariff = this.tariffs.find(t => t.id === selected.tariffId)
                if (tariff) {
                    currencySet.add(tariff.currencyCode)
                }
            })

            return currencySet.size <= 1
        }

        updateTariff(tariff) {
            if (tariff?.linkId) delete tariff.linkId
        }

        get contractServiceType() {
            return contractServiceType
        }
    }

    export default ProductPricesPenaltiesModal
</script>
